import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import { Observable, combineLatest, debounceTime, map } from 'rxjs';

import { assert } from 'common';
import { Search, SearchPrompt } from 'models';
import { SearchRouteParametersTuple } from 'src/app/models';

import { ArchivesQuery } from '../archives/archives.query';

import { decodeBase64 } from 'src/app/common/utility';
import { SearchesState, SearchesStore } from './searches.store';

/** Searches Query. */
@Injectable({ providedIn: 'root' })
export class SearchesQuery extends QueryEntity<SearchesState> {
  /** Observable of the active search or undefined if there is no active search. */
  active$ = this.selectActive();
  /** Observable of the active search id or undefined if there is not active search. */
  activeId$ = this.selectActiveId();
  /** Observable array of all searches in the active database. */
  all$ = this.selectAll();
  /** Observable of dashboard searches. */
  dashboardSearches$ = this.selectAll().pipe(
    map((searches) =>
      searches.filter((search) => search.settings.isDashboardSearch)
    )
  );
  /** Observable of favorited searches. */
  favoritedSearches$ = this.selectAll().pipe(
    map((searches) => searches.filter((search) => search.isFavorite))
  );
  /** Observable of dashboard search filter. */
  filter$ = this.routerQuery.selectQueryParams<string>('filter');
  /** Observable of searches loading state. */
  isLoading$ = this.selectLoading();
  /** Observable of current search prompts. */
  searchPromptParams$ = this.routerQuery
    .selectQueryParams<string>('prompts')
    .pipe(map((promptString) => this.parseSearchPrompt(promptString)));

  /** Observable view tab id for the currently active view tab or undefined if no view tab is loaded. */
  searchViewTabId$ = this.routerQuery.selectQueryParams('viewTab').pipe(
    map((viewTabId) => {
      viewTabId = viewTabId ? Number(viewTabId) : undefined;
      assert(typeof viewTabId === 'number' || typeof viewTabId === 'undefined');
      return viewTabId;
    })
  );
  /**
   * Search route parameters.
   *
   * @description Observable of Datbase, Archive, Search ID, SearchPrompt[], and view tab ID tuple.
   */
  searchRouteParams$: Observable<SearchRouteParametersTuple> = combineLatest([
    this.routerQuery.selectParams(['dbId', 'archiveId', 'searchId']),
    this.searchViewTabId$,
    this.searchPromptParams$,
  ]).pipe(
    debounceTime(1),
    map(([[databaseId, archiveId, searchId], viewTabId, searchPrompts]) => {
      // Ensure all values are either number or undefined.
      databaseId = databaseId ? Number(databaseId) : undefined;
      archiveId = archiveId ? Number(archiveId) : undefined;
      searchId = searchId ? Number(searchId) : undefined;
      viewTabId = viewTabId ? Number(viewTabId) : undefined;
      assert(
        typeof databaseId === 'number' || typeof databaseId === 'undefined'
      );
      assert(typeof archiveId === 'number' || typeof archiveId === 'undefined');
      assert(typeof searchId === 'number' || typeof searchId === 'undefined');
      assert(typeof viewTabId === 'number' || typeof viewTabId === 'undefined');
      return [
        databaseId ? Number(databaseId) : databaseId,
        archiveId,
        searchId,
        searchPrompts,
        viewTabId,
      ];
    })
  );

  constructor(
    protected store: SearchesStore,
    private archives: ArchivesQuery,
    private routerQuery: RouterQuery
  ) {
    super(store);
  }

  /**
   * Currently active search.
   *
   * @returns Search.
   * @throws {TypeError} If no search is currently active.
   */
  get active(): Search {
    return this.getSearch(this.activeId);
  }

  /**
   * Currently active search Id number.
   *
   * @returns Search Id number.
   * @throws {TypeError} If current state archive Id does not return a number.
   */
  get activeId(): number {
    const id = this.getActiveId();
    if (typeof id !== 'number') {
      throw new TypeError('Search Id was not a number.');
    }
    return id;
  }

  /** Get the current search prompts.
   *
   * @returns An array of search prompts.
   */
  get currentSearchPrompts(): SearchPrompt[] {
    const promptsString =
      this.routerQuery.getQueryParams<string>('prompts') ?? '';
    return this.parseSearchPrompt(promptsString);
  }

  /** Get the current filter.
   *
   * @returns A filter string.
   */
  get filter(): string {
    return this.routerQuery.getQueryParams<string>('filter') ?? '';
  }

  /**
   * Gets the view tab id for the currently active view tab or undefined if no view tab is in the route.
   *
   * @returns A view tab id or undefined if no view tab is present in the route.
   */
  get searchViewTabId(): number | undefined {
    return this.routerQuery.getQueryParams<number>('viewTab') ?? undefined;
  }

  /**
   * Get the default search for an archive.
   *
   * @param archiveId Archive ID.
   * @returns A search.
   */
  getDefault(archiveId: number = this.archives.activeId) {
    return this.getAll().find(
      (search: Search) => search.isDefault && search.parentArchive === archiveId
    );
  }

  /**
   * Get a search.
   *
   * @param searchId Search Id.
   * @returns Search.
   * @throws {RangeError} If the search is not found.
   */
  getSearch(searchId: number = this.activeId): Search {
    const search = this.getEntity(searchId);
    if (typeof search === 'undefined') {
      throw new RangeError('Search was not found for the provided Id..');
    }
    return search;
  }

  /**
   * Select the list of searches defined for a given archive.
   *
   * @param archiveId Archive ID.
   * @returns Observable list of searches.
   */
  selectForParent(archiveId: number) {
    return this.selectAll({
      filterBy: ({ parentArchive }) => parentArchive === archiveId,
    });
  }

  /**
   * Decode search prompts from a query string parameter into an array of SearchPrompt objects.
   *
   * The prompt string is decoded from base64 if it does not start with a '[' (indicating it is already unencoded).
   *
   * @param promptString Encoded query string representation of search prompts.
   * @returns An array of SearchPrompt objects.
   */
  private parseSearchPrompt(promptString: string): SearchPrompt[] {
    if (!promptString) {
      return [];
    }
    if (!promptString.startsWith('[')) {
      promptString = decodeBase64(promptString);
    }
    let parsedPrompts = JSON.parse(promptString) as SearchPrompt[];
    // Handle prompts that do not provide an id, but do provide a prompt name by
    // looking up prompt ids from the search's prompts and populating
    // (and duplicating if needed) entries for each id that matches the prompt
    // on that search.
    parsedPrompts = parsedPrompts.flatMap((prompt) => {
      if ((prompt.id === undefined || prompt.id === null) && prompt.prompt) {
        const matchingPrompts = this.active.parameters.filter(
          (p) => p.prompt === prompt.prompt
        );
        if (matchingPrompts.length > 0) {
          return matchingPrompts.map((p) => ({ ...prompt, id: p.id }));
        }
      }
      return prompt;
    });
    return parsedPrompts;
  }
}
